import eventNames from '@/features/common/constants/events';
import eventBus from '@/features/common/eventBus';
import { Vue, Component } from 'vue-property-decorator';
import { AvailableService, InProgress } from '../../types';
import { isValidInProgressData } from '../../helpers/createShipment';

@Component
export class ProgressbarMixin extends Vue {
  private SHIPMENT_CREATE_STEP = 1;

  private SHIPMENT_CUSTOMS_STEP = 2;

  private SHIPMENT_RESULTS_STEP = 3;

  private SHIPMENT_CHECKOUT_STEP = 4;

  private steps = {
    'shipment-create': this.SHIPMENT_CREATE_STEP,
    'shipment-customs': this.SHIPMENT_CUSTOMS_STEP,
    'shipment-results': this.SHIPMENT_RESULTS_STEP,
    'shipment-checkout': this.SHIPMENT_CHECKOUT_STEP,
  }

  get isInProgressDataChanged(): boolean {
    return this.$store.state.shipment.isInProgressDataChanged;
  }

  get isSelectedService(): boolean {
    return !!this.$store.getters.getSelectedService;
  }

  get availableServices(): AvailableService[] | null {
    return this.$store.getters.getAvailableServices;
  }

  get isCommercialInvoiceNeeded(): boolean {
    return this.$store.getters.getIsCommercialInvoice;
  }

  get isCheckoutVisible(): boolean {
    const isShowSummaryPage = this.$store.state.shipment.showSummaryPage;
    if (isShowSummaryPage) return true;

    const preferences = this.$store.getters.getUserPreferences;
    if (!preferences) return true;
    return preferences.shippingSettings.showSummaryPage;
  }

  get currentPageRoute(): string | null | undefined {
    return this.$route.name;
  }

  get currentPageStep(): number {
    if (!this.currentPageRoute) return 0;
    return this.steps[this.currentPageRoute];
  }

  get isInProgressDataValid(): boolean {
    const inProgressData: InProgress = this.$store.getters.getInProgress;
    return !!inProgressData && isValidInProgressData(inProgressData).valid;
  }

  get hasAvailableServices(): boolean {
    return !!this.availableServices?.length;
  }

  get isCreateShipmentEnabled(): boolean {
    return this.currentPageStep !== this.SHIPMENT_CREATE_STEP;
  }

  get isCustomsEnabled(): boolean {
    if (!this.isCommercialInvoiceNeeded) return false;
    if (this.currentPageStep === this.SHIPMENT_CUSTOMS_STEP) return false;
    if (this.currentPageStep < this.SHIPMENT_CUSTOMS_STEP) return this.isInProgressDataValid;
    return true;
  }

  get isResultsEnabled(): boolean {
    if (this.currentPageStep === this.SHIPMENT_RESULTS_STEP) return false;
    if (this.currentPageStep > this.SHIPMENT_RESULTS_STEP) return this.hasAvailableServices;
    return this.isInProgressDataValid && this.hasAvailableServices;
  }

  get isCheckoutEnabled(): boolean {
    if (this.currentPageStep === this.SHIPMENT_CHECKOUT_STEP) return false;
    if (this.isInProgressDataChanged) return false;
    return this.isResultsEnabled && this.isSelectedService;
  }

  public visitCreateShipmentPage(): void {
    if (!this.isCreateShipmentEnabled) return;
    this.$router.push({ name: 'shipment-create' });
  }

  public visitCustomsPage(): void {
    if (!this.isCustomsEnabled) return;
    if (this.currentPageStep === this.SHIPMENT_CREATE_STEP) {
      eventBus.$emit(eventNames.SHIPMENT_SUBMIT);
      return;
    }
    this.$router.push({ name: 'shipment-customs' });
  }

  public visitCheckoutPage(): void {
    if (!this.isCheckoutEnabled) return;
    this.$router.push({ name: 'shipment-checkout' });
  }

  public visitResultsPage(): void {
    if (this.currentPageRoute === 'shipment-checkout' || this.currentPageRoute === 'shipment-verify') {
      this.$router.push({ name: 'shipment-results' });
      return;
    }
    eventBus.$emit(eventNames.SHIPMENT_SUBMIT);
  }
}
